import api from './api';
import {
  ContentItem,
  ContentListResponse,
  ContentData,
  ContentFilters,
  ContentSortBy,
  SortOrder,
  SocialPlatform,
  ContentFormat,
  SchedulingOptions,
  PublishResponse,
  SocialAccount,
  ContentDashboardStats,
  CalendarEvent,
  PlatformContentOverride,
  ContentTone,
  ContentStatus
} from '../types/contentHub';

// Backend uses /social for social media operations (api.ts baseURL already has /api)
const SOCIAL_URL = '/social';

// Content Hub Service - connects to backend social media APIs
export const contentHubService = {
  // ============ SOCIAL ACCOUNTS ============

  // Get all connected social accounts
  async getConnectedAccounts(): Promise<SocialAccount[]> {
    const response = await api.get(`${SOCIAL_URL}/accounts`);
    if (response.data.success) {
      return response.data.data.map(mapBackendAccountToFrontend);
    }
    return [];
  },

  // Connect a new social account
  async connectAccount(platform: SocialPlatform, data: {
    username: string;
    access_token: string;
    refresh_token?: string;
  }): Promise<SocialAccount> {
    const response = await api.post(`${SOCIAL_URL}/accounts`, {
      platform,
      ...data
    });
    return mapBackendAccountToFrontend(response.data.data);
  },

  // Disconnect a social account
  async disconnectAccount(accountId: string): Promise<void> {
    await api.delete(`${SOCIAL_URL}/accounts/${accountId}`);
  },

  // Sync/refresh account data
  async syncAccount(accountId: string): Promise<SocialAccount> {
    const response = await api.post(`${SOCIAL_URL}/accounts/${accountId}/sync`);
    return mapBackendAccountToFrontend(response.data.data);
  },

  // ============ CONTENT/POSTS ============

  // Get content list (posts)
  async getContentList(
    filters?: ContentFilters,
    page: number = 1,
    pageSize: number = 20
  ): Promise<ContentListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: pageSize.toString()
    });

    if (filters?.status?.length) {
      params.append('status', filters.status[0]);
    }
    if (filters?.platforms?.length) {
      params.append('platform', filters.platforms[0]);
    }
    if (filters?.search) {
      params.append('search', filters.search);
    }

    const response = await api.get(`${SOCIAL_URL}/posts?${params}`);

    if (response.data.success) {
      return {
        items: response.data.data.map(mapBackendPostToContentItem),
        total: response.data.total || response.data.data.length,
        page: response.data.page || page,
        pageSize: response.data.limit || pageSize,
        hasMore: response.data.hasMore || false
      };
    }

    return { items: [], total: 0, page: 1, pageSize: 20, hasMore: false };
  },

  // Get single content item
  async getContent(contentId: string): Promise<ContentItem> {
    const response = await api.get(`${SOCIAL_URL}/posts/${contentId}`);
    return mapBackendPostToContentItem(response.data.data);
  },

  // Create new content/post
  async createContent(data: {
    content: ContentData;
    platforms: SocialPlatform[];
    format: ContentFormat;
    scheduledAt?: Date;
  }): Promise<ContentItem> {
    const response = await api.post(`${SOCIAL_URL}/posts`, {
      content: data.content.text,
      platforms: data.platforms.join(','),
      media_urls: data.content.media.map(m => m.url),
      hashtags: data.content.hashtags,
      scheduled_at: data.scheduledAt?.toISOString()
    });
    return mapBackendPostToContentItem(response.data.data);
  },

  // Update content
  async updateContent(contentId: string, data: Partial<{
    content: ContentData;
    platforms: SocialPlatform[];
  }>): Promise<ContentItem> {
    const updateData: any = {};
    if (data.content) {
      updateData.content = data.content.text;
      updateData.hashtags = data.content.hashtags;
      updateData.media_urls = data.content.media?.map(m => m.url);
    }
    if (data.platforms) {
      updateData.platforms = data.platforms.join(',');
    }

    const response = await api.put(`${SOCIAL_URL}/posts/${contentId}`, updateData);
    return mapBackendPostToContentItem(response.data.data);
  },

  // Delete content
  async deleteContent(contentId: string): Promise<void> {
    await api.delete(`${SOCIAL_URL}/posts/${contentId}`);
  },

  // Publish content
  async publishNow(contentId: string): Promise<PublishResponse> {
    const response = await api.post(`${SOCIAL_URL}/posts/${contentId}/publish`);
    return {
      success: response.data.success,
      contentId,
      results: [],
      failedPlatforms: []
    };
  },

  // Schedule content
  async scheduleContent(contentId: string, scheduling: SchedulingOptions): Promise<PublishResponse> {
    const response = await api.put(`${SOCIAL_URL}/posts/${contentId}`, {
      scheduled_at: scheduling.scheduledDate?.toISOString(),
      status: 'scheduled'
    });
    return {
      success: response.data.success,
      contentId,
      results: [],
      failedPlatforms: []
    };
  },

  // ============ STATS & ANALYTICS ============

  // Get dashboard stats
  async getDashboardStats(): Promise<ContentDashboardStats> {
    const response = await api.get(`${SOCIAL_URL}/stats`);

    if (response.data.success) {
      const stats = response.data.data;
      return {
        totalPosts: stats.totalPosts || 0,
        publishedThisWeek: stats.totalPosts || 0, // Simplificado
        scheduledCount: stats.scheduledPosts || 0,
        draftsCount: 0,
        platformBreakdown: {
          instagram: 0,
          facebook: 0,
          linkedin: 0,
          twitter: 0,
          youtube: 0,
          youtube_shorts: 0,
          tiktok: 0,
          threads: 0
        },
        topPerformingPosts: [],
        recentActivity: []
      };
    }

    return getEmptyDashboardStats();
  },

  // Get analytics
  async getAnalytics(period: '7d' | '30d' | '90d' = '30d'): Promise<{
    platformMetrics: any[];
    topPosts: ContentItem[];
    overview: any;
  }> {
    const response = await api.get(`${SOCIAL_URL}/analytics?period=${period}`);

    if (response.data.success) {
      return {
        platformMetrics: response.data.data.platformMetrics || [],
        topPosts: (response.data.data.topPosts || []).map(mapBackendPostToContentItem),
        overview: response.data.data.overview || {}
      };
    }

    return { platformMetrics: [], topPosts: [], overview: {} };
  },

  // ============ AI FEATURES ============

  // Generate content with AI (text only)
  async generateContent(params: {
    prompt: string;
    platforms: SocialPlatform[];
    tone: ContentTone;
    targetAudience?: string;
    includeHashtags?: boolean;
    includeEmojis?: boolean;
  }): Promise<{
    text: string;
    hashtags: string[];
    variations: string[];
  }> {
    const response = await api.post(`${SOCIAL_URL}/generate-caption`, {
      topic: params.prompt,
      tone: params.tone,
      platform: params.platforms[0]
    });

    if (response.data.success) {
      return {
        text: response.data.data.caption,
        hashtags: response.data.data.hashtags || [],
        variations: []
      };
    }

    throw new Error('Failed to generate content');
  },

  // Generate full post with AI (text + image)
  async generateFullPost(params: {
    prompt: string;
    platforms: SocialPlatform[];
    tone: ContentTone;
    targetAudience?: string;
    includeHashtags?: boolean;
    includeEmojis?: boolean;
    generateImage?: boolean;
    imageStyle?: 'realistic' | 'illustration' | 'minimalist' | '3d' | 'creative';
  }): Promise<{
    text: string;
    hashtags: string[];
    imageUrl: string | null;
    imagePrompt?: string;
    imageError?: string;
    platformContent?: Record<string, { text: string; charCount: number; charLimit: number }>;
  }> {
    const response = await api.post(`${SOCIAL_URL}/generate-full-post`, {
      prompt: params.prompt,
      platforms: params.platforms,
      tone: params.tone,
      targetAudience: params.targetAudience,
      includeHashtags: params.includeHashtags ?? true,
      includeEmojis: params.includeEmojis ?? true,
      generateImage: params.generateImage ?? true,
      imageStyle: params.imageStyle || 'realistic'
    });

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to generate content');
  },

  // Get AI suggestions
  async getSuggestions(): Promise<any[]> {
    const response = await api.get(`${SOCIAL_URL}/suggestions`);
    if (response.data.success) {
      return response.data.data;
    }
    return [];
  },

  // Generate new AI suggestions
  async generateSuggestions(params: {
    count?: number;
    platforms?: SocialPlatform[];
  }): Promise<any[]> {
    const response = await api.post(`${SOCIAL_URL}/suggestions/generate`, params);
    if (response.data.success) {
      return response.data.data;
    }
    return [];
  },

  // Discard suggestion
  async discardSuggestion(suggestionId: string): Promise<void> {
    await api.delete(`${SOCIAL_URL}/suggestions/${suggestionId}`);
  },

  // Regenerate suggestion
  async regenerateSuggestion(suggestionId: string): Promise<any> {
    const response = await api.post(`${SOCIAL_URL}/suggestions/${suggestionId}/regenerate`);
    return response.data.data;
  }
};

// ============ HELPER FUNCTIONS ============

// Map backend social account to frontend format
function mapBackendAccountToFrontend(account: any): SocialAccount {
  return {
    id: account.id?.toString() || account._id,
    platform: account.platform as SocialPlatform,
    accountName: account.username || account.name || 'Account',
    accountHandle: account.username || '',
    profileImage: account.profile_image || account.avatar_url,
    isConnected: account.connected !== false,
    connectionStatus: account.connected ? 'connected' : 'expired',
    lastSynced: account.last_sync ? new Date(account.last_sync) : undefined,
    permissions: [],
    metadata: {
      followers: account.followers
    }
  };
}

// Map backend post to frontend ContentItem
function mapBackendPostToContentItem(post: any): ContentItem {
  const platforms = typeof post.platforms === 'string'
    ? post.platforms.split(',').filter(Boolean) as SocialPlatform[]
    : (post.platforms || []) as SocialPlatform[];

  return {
    id: post.id?.toString() || post._id,
    projectId: post.project_id?.toString() || '',
    content: {
      text: post.content || '',
      media: (post.media_urls || []).map((url: string, i: number) => ({
        id: `media_${i}`,
        type: 'image' as const,
        url,
        fileName: '',
        fileSize: 0,
        status: 'ready' as const
      })),
      hashtags: post.hashtags || [],
      mentions: []
    },
    platforms,
    format: 'post' as ContentFormat,
    status: (post.status || 'draft') as ContentStatus,
    createdAt: new Date(post.created_at || Date.now()),
    updatedAt: new Date(post.updated_at || post.created_at || Date.now()),
    scheduledFor: post.scheduled_at ? new Date(post.scheduled_at) : undefined,
    publishedAt: post.published_at ? new Date(post.published_at) : undefined,
    platformResults: [],
    tags: [],
    createdBy: post.user_id?.toString() || ''
  };
}

// Empty dashboard stats
function getEmptyDashboardStats(): ContentDashboardStats {
  return {
    totalPosts: 0,
    publishedThisWeek: 0,
    scheduledCount: 0,
    draftsCount: 0,
    platformBreakdown: {
      instagram: 0,
      facebook: 0,
      linkedin: 0,
      twitter: 0,
      youtube: 0,
      youtube_shorts: 0,
      tiktok: 0,
      threads: 0
    },
    topPerformingPosts: [],
    recentActivity: []
  };
}

export default contentHubService;
